/**
 * abox-render-prep.ts — render-prep adapter (Phase 046 D-046-01/02 + Phase
 * 047-02 PREVIEW-03 flow-fold).
 *
 * The Phase 045 loader (`abox-loaded-app-loader.ts`) is a pure DECODE layer: it
 * inverts the platform's `add_block` triple decomposition into a `LoadedApp`,
 * but produces only a minimal best-effort `ui.root` (`type` + maybe
 * `text`/`placeholder`) — D-045-08 explicitly deferred a faithful renderable
 * `ui` to Phase 046.
 *
 * RESEARCH proved (D-046-02 RESOLVED) that a persisted block's `properties`
 * envelope is CONFIG-ONLY — it carries NO `ui` tree, no children, no layout.
 * So a renderable `ui` MUST be synthesized. `prepareAboxAppForRender` is a pure
 * `LoadedApp → LoadedApp` transform that post-processes the loader output: for
 * each ABox-loaded block it reads the verbatim decoded envelope the loader
 * stashed at `block.data.__aboxEnvelope` and synthesizes a faithful renderable
 * `ui` keyed on `block_type`. It is wired between `loader.load()` and
 * `openAboxApp` in `abox-app-deeplink.ts` (Plan 02).
 *
 * Keeping this OUT of the loader honors the CONTEXT's recommended (not
 * mandated) split — the loader stays the pure decode layer; the loader's
 * D-045-07 round-trip suite reads `__aboxEnvelope`, never `ui`, so it is
 * unaffected. The `createAboxLoadedAppLoader` export signature is byte-stable
 * (D-045-01) — this file does not touch it.
 *
 * `block_type → ui-template` coverage (RESEARCH §"D-046-02 RESOLVED"): three
 * structural template classes cover the observed `block_type`s (`text` /
 * `input` / `list`) plus a generic-container fallback for any unrecognized
 * type — coherent, NEVER an empty/placeholder render (D-046-02). Synthesized
 * `ui.root.type` is always a registered renderer element type (`text` /
 * `input` / `list` / `container` / `button` — verified valid keys in
 * `cogo-react-renderer/src/renderers/index.ts`).
 *
 * Expression pass-through: `properties.text` may carry `#{prefix.path}`
 * canonical-prefix expressions (the count block's `#{tasks.length} …`). The
 * string is passed through VERBATIM into the synthesized `ui` — the runtime
 * (`@cogo/runtime-v2` `StateManager.resolve`) resolves them.
 *
 * Phase 047-02 — flow-fold + tap-binding synthesis (PREVIEW-03). The loader
 * reverse-maps `kind:'flow'` subjects into a TOP-LEVEL `LoadedApp.flows` map,
 * but the renderer runtime (`@cogo/react-renderer-v2` `app-runtime.ts`)
 * registers flows ONLY from `block.flow.flows` (`registerSingleBlockFlows`) and
 * dispatches events ONLY from `block.events.bindings` (`registerBlockBindings`)
 * — it has NO top-level-`flows` concept. `toBlockDef` leaves every block's
 * `flow` / `events` `{}`, so the loaded flows are orphaned and no tap can fire
 * them. `prepareAboxAppForRender` therefore ALSO folds each loaded `Flow` into
 * its owning block's `block.flow.flows` and synthesizes the `_aboxTrigger`-
 * derived `events.bindings` tap entry — all in-lane (D-047-02; no `packages/`
 * edit, the runtime is consumed verbatim through its existing per-block path).
 *
 * Flow ownership + the Add-control id convention (RESEARCH Q1, load-bearing):
 *   • `toFlow` stashes the persisted trigger envelope at `flow._aboxTrigger`
 *     (`{ event, target }`). The `target` IS the stable control element id.
 *   • The owning block of a flow is the app's `input`-class block — the Add
 *     affordance host. (A task-list app has exactly one input block; the flow
 *     is the Add action that consumes the input's bound value.)
 *   • `synthesizeUi`'s `input` case emits only an `<input>` element — there is
 *     NO control for a `tap` binding to target. So when an `input`-class block
 *     OWNS a flow, the `input` case synthesizes a `container` root holding the
 *     `<input>` PLUS an Add `<button>` whose `id` is the flow's
 *     `_aboxTrigger.target` VERBATIM (the persisted target is already a stable
 *     element id — no re-derivation, no guessing). The button carries an
 *     accessible "Add" text so RTL `getByRole('button', { name: /add/i })`
 *     resolves it. This id-derivation convention is the contract the SC#1
 *     fixture (Task 2) mirrors.
 */

import type { BlockDef } from '@cogo/types-v2'
import { bindingKey } from '@cogo/types-v2'
import type {
  LoadedApp,
  PreviewSelectionRef,
  UiElement,
  UiTree,
} from '@/features/page-editor/store/types'

/**
 * The verbatim decoded platform block-def envelope the Phase 045 loader stashes
 * at `block.data.__aboxEnvelope` (`toBlockDef` ~L205-211). Exactly five
 * top-level keys — `properties` is a flat config bag.
 */
export interface AboxEnvelope {
  id: string
  kind: string
  block_name: string
  block_type: string
  properties: Record<string, unknown>
}

/**
 * Read the decoded envelope off a loaded block's `data`. Returns `null` when
 * the block was not produced by the ABox loader (no `__aboxEnvelope` stash) so
 * the caller can pass such a block through unchanged.
 */
export function readEnvelope(block: BlockDef): AboxEnvelope | null {
  // The persisted-envelope ↔ v2 `BlockDef['data']` shape mismatch is bridged by
  // a localized, documented `as unknown as` cast — the sanctioned type-bridge
  // (CLAUDE.md Bug-Fix rules: no magic numbers). It mirrors `toBlockDef`'s
  // stash-side cast in abox-loaded-app-loader.ts.
  const data = block.data as unknown as Record<string, unknown> | undefined
  const env = data?.__aboxEnvelope as AboxEnvelope | undefined
  if (!env || typeof env !== 'object') return null
  if (typeof env.block_type !== 'string') return null
  return {
    id: String(env.id ?? ''),
    kind: String(env.kind ?? ''),
    block_name: String(env.block_name ?? ''),
    block_type: env.block_type,
    properties:
      env.properties && typeof env.properties === 'object'
        ? (env.properties as Record<string, unknown>)
        : {},
  }
}

/** A JSON scalar — the leaf values the generic-container fallback renders. */
function isScalar(v: unknown): v is string | number | boolean {
  return (
    typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
  )
}

/**
 * The generic-container fallback children: one `text` element per SCALAR leaf
 * of `properties`, in declaration order. Carries the operator's authored
 * values — coherent, never empty (D-046-02's "no empty/placeholder render").
 */
function scalarLeavesAsText(
  blockName: string,
  properties: Record<string, unknown>,
): UiElement[] {
  return Object.entries(properties)
    .filter(([, v]) => isScalar(v))
    .map(([key, v]) => ({
      id: `${blockName}-${key}`,
      type: 'text',
      text: `${key}: ${String(v)}`,
    }))
}

/**
 * The deterministic synthesized `<input>` element id for an `input`-class
 * block — `${blockName}-field`. This is the contract a `change`-event flow's
 * `_aboxTrigger.target` mirrors (Phase 047-02 flow-fold; the SC#1 fixture
 * authors its `change` flow against this id).
 */
function inputFieldId(blockName: string): string {
  return `${blockName}-field`
}

/**
 * Synthesize a renderable `UiTree` from a block's `block_type` + decoded
 * `properties`. The `switch` covers the observed structural template classes;
 * `default` is the generic-container fallback for any unrecognized type.
 *
 * `tapControlIds` — when non-empty (the block owns one or more `tap`-triggered
 * flows, Phase 047-02 flow-fold), the `input` case ALSO synthesizes one
 * `<button>` per id so each synthesized `events.bindings` tap entry has a real
 * control to target. The button id is the flow's `_aboxTrigger.target`
 * verbatim — no re-derivation.
 */
export function synthesizeUi(
  blockType: string,
  blockName: string,
  properties: Record<string, unknown>,
  tapControlIds?: readonly string[],
  // Phase 048 D-048-08 — forward-map accumulator + persisted block subject IRI
  // for the value's `blockId` field. Optional so the function stays callable
  // from any prior test/harness that did not pre-allocate the map.
  map?: Record<string, PreviewSelectionRef>,
  blockId?: string,
): UiTree {
  const rootId = `${blockName}-root`
  // Skip emission when the accumulator was not supplied (legacy callers).
  // `prepareAboxAppForRender` always supplies it.
  const emit = (id: string, propertyKey: string): void => {
    if (!map || !blockId) return
    map[id] = { blockId, blockName, blockType, propertyKey }
  }

  switch (blockType) {
    case 'text':
    case 'heading': {
      // text-class — `properties.text` (may carry #{...} expressions, verbatim)
      const root: UiElement = {
        id: rootId,
        type: blockType,
        text: String(properties.text ?? ''),
      }
      // D-048-08 — the text root is the persisted `properties.text` element.
      emit(rootId, 'text')
      return { schemaVersion: '3.0', blockType, root }
    }

    case 'input': {
      // input-class — placeholder + binding.
      const inputEl: UiElement = {
        id: inputFieldId(blockName),
        type: 'input',
        placeholder: String(properties.placeholder ?? ''),
      }
      if (properties.binding != null) {
        inputEl.bind = String(properties.binding)
      }
      // No owned tap-flow → a bare <input> root (Phase 046 shape, byte-stable
      // — the input root id stays `${blockName}-root`).
      if (!tapControlIds || tapControlIds.length === 0) {
        const root: UiElement = {
          id: rootId,
          type: 'input',
          placeholder: inputEl.placeholder,
        }
        if (inputEl.bind != null) root.bind = inputEl.bind
        // D-048-08 — primary property is `placeholder` (RESEARCH A1 — `binding`
        // is logic-wiring, deferred to Phase 050 scope).
        emit(rootId, 'placeholder')
        return { schemaVersion: '3.0', blockType, root }
      }
      // Owns a tap-flow — synthesize a container holding the <input> + one
      // Add <button> per tap-control id. Each button id is the owning flow's
      // `_aboxTrigger.target` verbatim (the flow-fold contract); its "Add"
      // text gives it the accessible name RTL
      // `getByRole('button', { name: /add/i })` resolves.
      const addButtons: UiElement[] = tapControlIds.map((id) => ({
        id,
        type: 'button',
        text: 'Add',
      }))
      const root: UiElement = {
        id: rootId,
        type: 'container',
        children: [inputEl, ...addButtons],
      }
      // D-048-08 — map entry is on the `<input>` (the property element), NOT
      // the wrapper container `rootId` and NOT the Add buttons. Both the
      // container and the Add buttons are synthesized chrome (the buttons'
      // `text:'Add'` is hardcoded, not a persisted property — D-048-05).
      emit(inputFieldId(blockName), 'placeholder')
      return { schemaVersion: '3.0', blockType, root }
    }

    case 'list': {
      // list-class — the `iterate_over` data binding + the per-row item
      // template. The synthesized shape must match the `ListRenderer` contract
      // (verified against `cogo-react-renderer/src/renderers/list-renderer.tsx`):
      //   • `options.data` is passed to `resolveSymbol`, which resolves ONLY
      //     `#{...}` expressions — a bare path resolves to itself (a string),
      //     never to the bound array. The `iterate_over` path is therefore
      //     wrapped as `#{<path>}` so it reaches the renderer as an expression.
      //   • `ListRenderer` reads the per-row item template from
      //     `options.itemRef` (a `UiElement`), NOT from `element.children` —
      //     `children` is never read by `ListRenderer`, so the synthesized list
      //     `root` carries no `children` key. The item template's `{{item.X}}`
      //     mustache is translated to the renderer's `#{item.X}` expression
      //     syntax (the only per-row interpolation `ListRenderer` understands).
      //   • The synthesized list `root` carries NO `iterateOver` field
      //     (Phase 047-02 Rule-1 fix). `ElementRenderer` intercepts ANY element
      //     with `iterateOver` BEFORE the registry lookup (element-renderer.tsx
      //     L80) and resolves that field as the data source — bypassing
      //     `ListRenderer` entirely. With `iterateOver` set to the BARE path
      //     `'tasks'`, `resolveSymbol('tasks')` returns the literal string
      //     (not the array), `Array.isArray` is false, and `ElementRenderer`
      //     returns `null` — the list renders nothing at runtime. Emitting
      //     ONLY `options.{data,itemRef}` lets the `list` element reach
      //     `ListRenderer` through the registry, which is the contract that
      //     actually renders the rows. (The Phase 047-01 SUMMARY assumed
      //     `root.iterateOver` was an inert "separate v2 schema field" — it is
      //     not inert; `ElementRenderer` consumes it first.)
      const iterateOver =
        properties.iterate_over != null
          ? String(properties.iterate_over)
          : undefined
      const itemTemplate =
        properties.item_template != null
          ? String(properties.item_template)
          : ''
      // translate the `{{item.X}}` mustache → `#{item.X}` (ListRenderer's
      // child-context resolver understands `#{...}` only).
      const itemRefText = itemTemplate.replace(
        /\{\{\s*(item\.[\w.]+)\s*\}\}/g,
        '#{$1}',
      )
      const itemNode: UiElement = {
        id: `${blockName}-item`,
        type: 'text',
        text: itemRefText,
      }
      const root: UiElement = {
        id: rootId,
        type: 'list',
      }
      if (iterateOver) {
        // `options` — the `ListRenderer` contract: `data` a `#{...}`
        // expression, the item template at `itemRef`. No `iterateOver` field
        // (see the comment above — it would make `ElementRenderer` bypass
        // `ListRenderer`).
        root.options = { data: `#{${iterateOver}}`, itemRef: itemNode }
      }
      // D-048-08 — two entries: the list root maps to `iterate_over`, the
      // item-template node maps to `item_template`. The painted DOM id of
      // each rendered row is `${blockName}-item-${listRootId}-<i>` (see
      // packages/cogo-react-renderer/src/renderers/list-renderer.tsx
      // `cloneElementWithIds`); the Preview selection layer normalizes that
      // suffix back to `${blockName}-item` before the map lookup (Plan 01
      // Task 3 — `normalizeListRowId`).
      emit(rootId, 'iterate_over')
      emit(itemNode.id, 'item_template')
      return { schemaVersion: '3.0', blockType, root }
    }

    default: {
      // generic-container fallback — coherent, never empty/placeholder.
      const root: UiElement = {
        id: rootId,
        type: 'container',
        children: scalarLeavesAsText(blockName, properties),
      }
      // D-048-08 — NO entry for the wrapper container `rootId` (synthesized
      // pure wrapper — D-048-05 → click deselects). One entry per scalar
      // leaf, mirroring `scalarLeavesAsText`'s id convention
      // `${blockName}-${key}`.
      for (const [key, v] of Object.entries(properties)) {
        if (isScalar(v)) {
          emit(`${blockName}-${key}`, key)
        }
      }
      return { schemaVersion: '3.0', blockType, root }
    }
  }
}

/**
 * The persisted flow trigger envelope `toFlow` stashes at `flow._aboxTrigger`
 * (`{ event, target }`). `target` is the stable control element id, `event`
 * the event type (e.g. `'tap'`).
 */
interface AboxTrigger {
  event: string
  target: string
}

/** Read a `Flow`'s `_aboxTrigger` stash; `null` if absent or malformed. */
function readTrigger(flow: unknown): AboxTrigger | null {
  if (!flow || typeof flow !== 'object') return null
  const trig = (flow as Record<string, unknown>)._aboxTrigger
  if (!trig || typeof trig !== 'object') return null
  const { event, target } = trig as Record<string, unknown>
  if (typeof event !== 'string' || typeof target !== 'string') return null
  if (!event || !target) return null
  return { event, target }
}

/** One loaded flow + its decoded trigger, resolved for the flow-fold. */
interface FoldedFlow {
  flowName: string
  flow: unknown
  trigger: AboxTrigger
}

/**
 * Pure render-prep adapter — synthesize a faithful renderable `ui` per
 * `block_type` for every ABox-loaded block in a `LoadedApp`, then fold each
 * top-level `LoadedApp.flows` entry into its owning block so the renderer
 * runtime can register + dispatch it (Phase 047-02 PREVIEW-03).
 *
 * Returns a NEW `LoadedApp` — the input app and its blocks are NOT mutated
 * (the loader output may be Immer-frozen; `PreviewFrameContent` clones it).
 * A block without an `__aboxEnvelope` stash (i.e. not loaded by the Phase 045
 * loader) is passed through with its `ui` unchanged.
 *
 * Flow-fold (PREVIEW-03): the renderer runtime registers flows ONLY from
 * `block.flow.flows` (`registerSingleBlockFlows`) and dispatches events ONLY
 * from `block.events.bindings` (`registerBlockBindings`); the loader leaves
 * both `{}`. This adapter folds EVERY loaded `Flow` into the app's single
 * `input`-class block — the interaction host:
 *   1. each `Flow` → a `block.flow.flows[flowName]` entry;
 *   2. each flow's `_aboxTrigger` → a `block.events.bindings` entry keyed
 *      `'<target>:<event>'` → `{ flow: flowName }`;
 *   3. a `tap`-triggered flow's `_aboxTrigger.target` becomes the id of a
 *      synthesized Add `<button>` (the input case emits one per tap-control);
 *      a non-`tap` flow (e.g. a `change`-triggered title-sync flow) targets
 *      the synthesized `<input>` element id (`${blockName}-field`).
 * All built objects are plain JSON literals so the folded block survives
 * `PreviewFrameContent.cloned`'s `JSON.parse(JSON.stringify(...))` deep-clone
 * (the `Flow` itself is plain — `toFlow` builds it from plain literals).
 */
export function prepareAboxAppForRender(loaded: LoadedApp): LoadedApp {
  // Resolve the app's single `input`-class block — the interaction host every
  // loaded flow folds into (a task-list-shaped app has exactly one).
  let inputOwnerName: string | null = null
  for (const [name, block] of Object.entries(loaded.blocks)) {
    if (readEnvelope(block)?.block_type === 'input') {
      inputOwnerName = name
      break
    }
  }

  // Collect every loaded flow with a usable `_aboxTrigger` (event + target).
  // A flow with no usable trigger cannot be wired to a control — it is skipped
  // rather than folded as a dangling, inert binding.
  const ownedFlows: FoldedFlow[] = []
  if (inputOwnerName) {
    for (const [flowName, flow] of Object.entries(loaded.flows ?? {})) {
      const trigger = readTrigger(flow)
      if (trigger) ownedFlows.push({ flowName, flow, trigger })
    }
  }
  // The `tap`-control ids the input case must synthesize an Add <button> for.
  const tapControlIds = ownedFlows
    .filter((f) => f.trigger.event === 'tap')
    .map((f) => f.trigger.target)

  const blocks: Record<string, BlockDef> = {}
  // Phase 048 D-048-08 — forward sidecar map `elementId → {blockId, blockName,
  // blockType, propertyKey}`. Allocated once, threaded into every
  // `synthesizeUi` call so each `case` records its entries forward. The
  // selection layer reads this from `currentProject.aboxElementMap`.
  const aboxElementMap: Record<string, PreviewSelectionRef> = {}

  for (const [name, block] of Object.entries(loaded.blocks)) {
    const env = readEnvelope(block)
    if (!env) {
      // Not an ABox-loaded block — pass through verbatim.
      blocks[name] = block
      continue
    }
    const ownsFlows = name === inputOwnerName && ownedFlows.length > 0
    const ui = synthesizeUi(
      env.block_type,
      env.block_name || name,
      env.properties,
      ownsFlows ? tapControlIds : undefined,
      aboxElementMap,
      env.id,
    )
    if (!ownsFlows) {
      // Replace ONLY `ui`; everything else (meta, events, flow, state, data —
      // including the `__aboxEnvelope` stash) is carried verbatim. A fresh
      // object so the loader's (possibly frozen) output is never mutated.
      blocks[name] = { ...block, ui } as BlockDef
      continue
    }
    // This block owns the app's flows — fold them in. `block.flow.flows` is
    // what `registerSingleBlockFlows` reads; `block.events.bindings` keyed
    // `<elementId>:<eventType>` is what `registerBlockBindings` reads. All
    // plain-JSON literals so the folded block survives the `PreviewFrameContent`
    // JSON deep-clone.
    const flows: Record<string, unknown> = {}
    const bindings: Record<string, { flow: string }> = {}
    for (const f of ownedFlows) {
      flows[f.flowName] = f.flow
      bindings[bindingKey(f.trigger.target, f.trigger.event)] = { flow: f.flowName }
    }
    blocks[name] = {
      ...block,
      ui,
      flow: { flows },
      events: { bindings },
    } as unknown as BlockDef
  }

  return { ...loaded, blocks, aboxElementMap }
}
